import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { http, resolveImage } from "@/lib/api";
import Layout from "@/components/Layout";
import { CheckCircle2, Clock, Copy as CopyIcon, Printer, Download } from "lucide-react";
import { toast } from "sonner";

export default function ProofOfPurchase() {
  const { orderNumber } = useParams();
  const [order, setOrder] = useState(null);

  useEffect(() => {
    http.get(`/orders/by-number/${orderNumber}`).then(({ data }) => setOrder(data)).catch(() => {});
  }, [orderNumber]);

  if (!order) return <Layout><div className="py-24 text-center font-ui text-[#737373]">Loading…</div></Layout>;

  const paid = order.payment_status === "paid";
  const pending = order.payment_status === "manual_pending" || order.payment_status === "pending";
  const handleLabel = order.payment_method === "cashapp" ? "$MickeyTrivett" : "@MickeyTrivett";
  const methodLabel = order.payment_method === "cashapp" ? "Cash App" : order.payment_method === "venmo" ? "Venmo" : "Card";
  const placed = new Date(order.created_at);
  const placedDate = placed.toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
  const placedTime = placed.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });

  const copy = async (txt, label = "Copied") => { try { await navigator.clipboard.writeText(txt); toast.success(label); } catch { /* noop */ } };

  return (
    <Layout>
      {/* Print styles — hide nav/footer when printing */}
      <style>{`
        @media print {
          header, footer, .no-print { display: none !important; }
          body, html { background: #FBF7EC !important; }
          .stc-certificate { background: #FBF7EC !important; color: #1c1916 !important; box-shadow: none !important; border-color: #b88a4a !important; }
          .stc-certificate .text-cream, .stc-certificate h1, .stc-certificate h2, .stc-certificate h3 { color: #1c1916 !important; }
          .stc-certificate .text-muted { color: #5a5247 !important; }
        }
      `}</style>

      <section className="bg-black pt-12 pb-24">
        <div className="max-w-[920px] mx-auto px-4 md:px-6">
          {/* Action bar (no-print) */}
          <div className="no-print flex items-center justify-between mb-6">
            <Link to="/shop" className="font-ui text-[10px] tracking-[0.32em] uppercase text-[#9a9690] hover:text-[#C9A055]">
              ← Continue browsing
            </Link>
            <div className="flex items-center gap-2">
              <button onClick={() => window.print()} data-testid="proof-print-btn" className="inline-flex items-center gap-2 px-4 py-2 border border-[#C9A055] text-[#C9A055] hover:bg-[#C9A055] hover:text-black font-ui text-[10px] tracking-[0.32em] uppercase transition-colors">
                <Printer size={13}/> Print / Save PDF
              </button>
            </div>
          </div>

          {/* ====================== Certificate Card ====================== */}
          <article
            className="stc-certificate relative bg-[#0a0a0a] border border-[#C9A055]/40 text-[#F5F2EB] overflow-hidden"
            data-testid="proof-certificate"
          >
            {/* Inner subtle frame */}
            <div className="absolute inset-3 border border-[#C9A055]/15 pointer-events-none" aria-hidden/>

            {/* Header block */}
            <div className="px-8 md:px-14 pt-12 md:pt-14 text-center relative">
              {/* STC Monogram */}
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-full border border-[#C9A055] mb-4">
                <span className="font-display-saira text-[16px] tracking-[0.18em] text-[#C9A055]">STC</span>
              </div>
              <div className="font-display-saira text-lg md:text-xl tracking-[0.38em] text-[#F5F2EB]">SIMPLE TRUTHS CLUB</div>

              {/* Gold rule with diamond */}
              <div className="flex items-center justify-center gap-4 mt-4 mb-3">
                <span className="block w-24 md:w-40 h-px bg-[#C9A055]/60"/>
                <span className="w-1.5 h-1.5 bg-[#C9A055] rotate-45 inline-block"/>
                <span className="block w-24 md:w-40 h-px bg-[#C9A055]/60"/>
              </div>

              <div className="font-ui text-[10px] md:text-[11px] tracking-[0.42em] uppercase text-[#C9A055]">
                {paid ? "Certificate of Acquisition" : "Order Confirmation"}
              </div>

              {/* Order number stamped */}
              <h1 data-testid="proof-order-number"
                  className="font-display-saira text-4xl md:text-5xl text-[#F5F2EB] tracking-wider mt-6">
                {order.order_number}
              </h1>
              <button
                onClick={() => copy(order.order_number, "Order number copied")}
                className="no-print mt-2 inline-flex items-center gap-1.5 font-ui text-[10px] tracking-[0.22em] uppercase text-[#9a9690] hover:text-[#C9A055]"
                data-testid="copy-order-number"
              >
                <CopyIcon size={11}/> Copy
              </button>

              <p className="font-body italic text-[#C9A055] mt-5">
                {paid
                  ? "Paid. Your truth is on its way."
                  : pending
                    ? "Pending payment. Follow the instructions below."
                    : `Status: ${order.payment_status}`}
              </p>
            </div>

            {/* Manual payment block */}
            {pending && order.payment_method !== "stripe" && (
              <div className="mx-8 md:mx-14 mt-8 border border-[#C9A055]/50 bg-[#C9A055]/5 p-5 md:p-6" data-testid="manual-payment-card">
                <div className="flex items-start gap-3">
                  <Clock size={20} className="text-[#C9A055] mt-1 shrink-0"/>
                  <div className="flex-1">
                    <div className="font-ui text-[10px] tracking-[0.32em] uppercase text-[#C9A055]">Awaiting {methodLabel} Payment</div>
                    <p className="font-body text-[#F5F2EB] mt-2 leading-relaxed">
                      Send <span className="text-[#C9A055] font-semibold">${order.total.toFixed(2)}</span> to{" "}
                      <button onClick={() => copy(handleLabel, "Handle copied")} className="font-mono text-[#D4B07B] underline-offset-2 hover:underline">{handleLabel}</button>{" "}
                      on {methodLabel}.
                    </p>
                    <p className="font-body text-[#9a9690] mt-2 text-sm">
                      Include order number <button onClick={() => copy(order.order_number, "Order number copied")} className="text-[#F5F2EB] font-mono underline-offset-2 hover:underline">{order.order_number}</button> in the note. Mickey will mark your order paid within 24 hours.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {paid && (
              <div className="mx-8 md:mx-14 mt-8 border border-emerald-700/50 bg-emerald-900/15 p-5 md:p-6 flex items-start gap-3">
                <CheckCircle2 size={20} className="text-emerald-400 mt-1 shrink-0"/>
                <div>
                  <div className="font-ui text-[10px] tracking-[0.32em] uppercase text-emerald-400">Payment Received</div>
                  <p className="font-body text-[#F5F2EB] mt-1 leading-relaxed">Your order is being prepared. Tracking will be emailed when it ships.</p>
                </div>
              </div>
            )}

            {/* Customer / Placed meta */}
            <div className="mx-8 md:mx-14 mt-10 grid sm:grid-cols-2 gap-6 border-t border-[#C9A055]/15 pt-6">
              <div>
                <div className="font-ui text-[10px] tracking-[0.42em] uppercase text-[#C9A055] mb-2">Acquired By</div>
                <div className="font-display-saira text-lg text-[#F5F2EB]">{order.name || order.email}</div>
                <div className="font-body text-sm text-[#9a9690]">{order.email}</div>
              </div>
              <div className="sm:text-right">
                <div className="font-ui text-[10px] tracking-[0.42em] uppercase text-[#C9A055] mb-2">Placed</div>
                <div className="font-display-saira text-lg text-[#F5F2EB]">{placedDate}</div>
                <div className="font-body text-sm text-[#9a9690]">{placedTime}</div>
                {order.qr_location_name && (
                  <div className="font-ui text-[10px] tracking-[0.32em] uppercase text-[#C9A055] mt-2">
                    From Mobile Gallery · {order.qr_location_name}
                  </div>
                )}
              </div>
            </div>

            {/* Items — gallery card style */}
            <div className="mx-8 md:mx-14 mt-10">
              <div className="flex items-center justify-center gap-3 mb-6 text-[#C9A055]" aria-hidden>
                <span className="block w-12 h-px bg-[#C9A055]/40"/>
                <span className="font-ui text-[10px] tracking-[0.42em] uppercase">The Truths You&apos;re Bringing Home</span>
                <span className="block w-12 h-px bg-[#C9A055]/40"/>
              </div>

              <div className="space-y-5">
                {(order.items || []).map((it, i) => (
                  <div key={i} className="border border-[#C9A055]/20 bg-[#070707] p-4 md:p-5 flex gap-5" data-testid={`proof-item-${i}`}>
                    <div className="w-24 h-24 md:w-28 md:h-28 shrink-0 bg-black border border-white/5">
                      <img src={resolveImage(it.main_image)} alt="" className="w-full h-full object-contain p-1"/>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-display-saira text-xl md:text-2xl text-[#F5F2EB] tracking-wide">{it.title}</div>
                      <div className="font-ui text-[10px] tracking-[0.32em] uppercase text-[#C9A055] mt-1">
                        {it.variant_label} {it.quantity > 1 && <span className="text-[#9a9690]">· × {it.quantity}</span>}
                      </div>
                      {it.personal_text && (
                        <div className="font-script text-base text-[#D4B07B] mt-2 italic">&ldquo;{it.personal_text}&rdquo;</div>
                      )}
                      {it.is_digital && (
                        paid ? (
                          <a
                            data-testid={`download-digital-${it.artwork_id}`}
                            href={`${(process.env.REACT_APP_BACKEND_URL || "")}/api/orders/by-number/${order.order_number}/download/${it.artwork_id}`}
                            className="no-print inline-flex items-center gap-1.5 mt-3 px-3 py-1.5 border border-[#C9A055] text-[#C9A055] hover:bg-[#C9A055] hover:text-black font-ui text-[10px] tracking-[0.32em] uppercase transition-colors"
                            target="_blank" rel="noreferrer"
                          >
                            <Download size={12}/> Download Digital File
                          </a>
                        ) : (
                          <div className="mt-2 font-ui text-[10px] tracking-[0.32em] uppercase text-[#737373]">Digital download unlocks once payment is confirmed.</div>
                        )
                      )}
                    </div>
                    <div className="font-display-saira text-xl text-[#F5F2EB] self-start whitespace-nowrap">${(it.line_total).toFixed(2)}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Totals */}
            <div className="mx-8 md:mx-14 mt-8 border-t border-[#C9A055]/15 pt-5 space-y-2">
              <div className="flex justify-between font-body text-sm">
                <span className="text-[#9a9690]">Subtotal</span>
                <span className="text-[#F5F2EB] font-display-saira">${order.subtotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between font-body text-sm">
                <span className="text-[#9a9690]">Shipping</span>
                <span className="text-[#F5F2EB] font-display-saira">${order.shipping.toFixed(2)}</span>
              </div>
              <div className="flex justify-between pt-4 mt-2 border-t border-[#C9A055]/30 items-baseline">
                <span className="font-ui text-[11px] tracking-[0.42em] uppercase text-[#C9A055]">Total</span>
                <span className="font-display-saira text-3xl md:text-4xl text-[#C9A055]" data-testid="proof-total">${order.total.toFixed(2)}</span>
              </div>
              <div className="text-right font-ui text-[10px] tracking-[0.32em] uppercase text-[#9a9690]">
                {methodLabel}
              </div>
            </div>

            {/* Signature block */}
            <div className="mx-8 md:mx-14 mt-12 pt-8 border-t border-[#C9A055]/15 grid sm:grid-cols-2 gap-8 items-end">
              <div>
                <div className="font-ui text-[10px] tracking-[0.42em] uppercase text-[#C9A055] mb-2">A note from the artist</div>
                <p className="font-body italic text-[#B5B0A7] text-sm leading-relaxed">
                  Thank you for carrying a Simple Truth. Hang it where you need to remember.
                </p>
              </div>
              <div className="text-right">
                <div className="font-script text-4xl text-[#C9A055] -rotate-2 inline-block">— Mickey Trivett</div>
                <div className="font-ui text-[9px] tracking-[0.42em] uppercase text-[#9a9690] mt-1">Founder · Artist</div>
              </div>
            </div>

            {/* Footer stamp */}
            <div className="px-8 md:px-14 pt-10 pb-10 text-center">
              <div className="flex items-center justify-center gap-3 text-[#C9A055]/50 mb-3" aria-hidden>
                <span className="block w-20 h-px bg-[#C9A055]/30"/>
                <span className="w-1.5 h-1.5 bg-[#C9A055]/50 rotate-45 inline-block"/>
                <span className="block w-20 h-px bg-[#C9A055]/30"/>
              </div>
              <div className="font-ui text-[10px] tracking-[0.42em] uppercase text-[#9a9690]">simpletruthsclub.com</div>
              <div className="font-ui text-[9px] tracking-[0.32em] uppercase text-[#737373] mt-1">Keep this receipt — it is your proof of acquisition.</div>
            </div>
          </article>
        </div>
      </section>
    </Layout>
  );
}
